(() => {
	/**
	 * Gestione campi fiscali nel carrello
	 *
	 * Funzionalità:
	 * - Select "Seleziona documento" per scegliere tra "Codice Fiscale" e "Partita IVA".
	 * - Mostra solo il campo relativo alla scelta.
	 * - Imposta l'attributo HTML `required` in modo mutuamente esclusivo (solo sul campo visibile).
	 * - Sincronizza l'asterisco di obbligatorietà (span.required-field) in base a `required`.
	 * - Gestisce il back/forward del browser (bfcache) riallineando UI allo stato corrente.
	 * - Reinizializza dopo aggiornamenti AJAX del carrello (evento `cart-updated`).
	 */
	const syncFiscalDocumentFields = (root = document) => {
		const STORAGE_KEYS = {
			type: 'cart_fiscal_document_type',
			codiceFiscale: 'cart_codice_fiscale',
			partitaIva: 'cart_partita_iva',
			codiceSdi: 'cart_codice_sdi'
		};

		const canUseSessionStorage = (() => {
			try {
				const testKey = '__storage_test__';
				window.sessionStorage.setItem(testKey, '1');
				window.sessionStorage.removeItem(testKey);
				return true;
			} catch (_) {
				return false;
			}
		})();

		// Uso di `root.getElementById(...)` se disponibile, con fallback su `document`.
		// Serve perché in alcuni punti il tema rimpiazza porzioni di DOM del carrello.
		const getById = (id) => (root.getElementById?.(id) || document.getElementById(id));

		const documentTypeSelect = getById('documento-fiscale-tipo');
		const codiceFiscaleWrapper = getById('codice-fiscale-field');
		const partitaIvaWrapper = getById('partita-iva-field');
		const codiceSdiWrapper = getById('codice-sdi-field');
		const codiceFiscaleInput = getById('codice-fiscale');
		const partitaIvaInput = getById('partita-iva');
		const codiceSdiInput = getById('codice-sdi');
		const checkoutButton = getById('CheckOut');

		// Se i nodi non ci sono, significa che siamo su un template dove il blocco non esiste.
		if (!documentTypeSelect || !codiceFiscaleWrapper || !partitaIvaWrapper || !codiceSdiWrapper || !codiceFiscaleInput || !partitaIvaInput || !codiceSdiInput) return;

		// Ripristino valori precedentemente inseriti (sessionStorage) se disponibili.
		// Utile quando si naviga via link e si torna al carrello, perché i valori dei campi
		// non vengono salvati su Shopify finché non si aggiorna/checkout.
		const restoreFromSessionStorage = () => {
			if (!canUseSessionStorage) return;

			const savedType = window.sessionStorage.getItem(STORAGE_KEYS.type);
			const savedCodiceFiscale = window.sessionStorage.getItem(STORAGE_KEYS.codiceFiscale);
			const savedPartitaIva = window.sessionStorage.getItem(STORAGE_KEYS.partitaIva);
			const savedCodiceSdi = window.sessionStorage.getItem(STORAGE_KEYS.codiceSdi);

			// Ripristino select solo se il valore esiste tra le opzioni
			if (savedType && Array.from(documentTypeSelect.options).some(o => o.value === savedType)) {
				documentTypeSelect.value = savedType;
			}

			// Ripristino valori input solo se attualmente vuoti (così non sovrascrivo bfcache)
			if (savedCodiceFiscale && !codiceFiscaleInput.value) {
				codiceFiscaleInput.value = savedCodiceFiscale;
			}
			if (savedPartitaIva && !partitaIvaInput.value) {
				partitaIvaInput.value = savedPartitaIva;
			}
			if (savedCodiceSdi && !codiceSdiInput.value) {
				codiceSdiInput.value = savedCodiceSdi;
			}
		};

		const saveToSessionStorage = () => {
			if (!canUseSessionStorage) return;
			window.sessionStorage.setItem(STORAGE_KEYS.type, documentTypeSelect.value);
			window.sessionStorage.setItem(STORAGE_KEYS.codiceFiscale, (codiceFiscaleInput.value || '').trim().toUpperCase());
			window.sessionStorage.setItem(STORAGE_KEYS.partitaIva, (partitaIvaInput.value || '').trim().toUpperCase());
			window.sessionStorage.setItem(STORAGE_KEYS.codiceSdi, (codiceSdiInput.value || '').trim().toUpperCase());
		};

		restoreFromSessionStorage();

		// Funzione centrale: allinea visibilità, required e indicatori in base al valore attuale della select.
		const applyStateFromSelection = () => {
			const selectedValue = documentTypeSelect.value;
			const isCodiceFiscaleSelected = selectedValue === 'Codice Fiscale';
			const isPartitaIvaSelected = selectedValue === 'Partita IVA';

			// Visibilità dei campi
			codiceFiscaleWrapper.style.display = isCodiceFiscaleSelected ? '' : 'none';
			partitaIvaWrapper.style.display = isPartitaIvaSelected ? '' : 'none';
			codiceSdiWrapper.style.display = isPartitaIvaSelected ? '' : 'none';

			// Required mutuamente esclusivo: prima resetto, poi applico solo al campo scelto
			codiceFiscaleInput.removeAttribute('required');
			partitaIvaInput.removeAttribute('required');
			if (isCodiceFiscaleSelected) codiceFiscaleInput.setAttribute('required', 'required');
			if (isPartitaIvaSelected) partitaIvaInput.setAttribute('required', 'required');

			// Asterischi: li mostro solo se il relativo input è required
			const codiceFiscaleRequiredIndicator = document.querySelector('[data-required-indicator="codice-fiscale"]');
			const partitaIvaRequiredIndicator = document.querySelector('[data-required-indicator="partita-iva"]');
			if (codiceFiscaleRequiredIndicator) {
				codiceFiscaleRequiredIndicator.style.display = codiceFiscaleInput.hasAttribute('required') ? '' : 'none';
			}
			if (partitaIvaRequiredIndicator) {
				partitaIvaRequiredIndicator.style.display = partitaIvaInput.hasAttribute('required') ? '' : 'none';
			}

			// Persisto lo stato ad ogni riallineamento
			saveToSessionStorage();
		};

		// IMPORTANT: questa chiamata deve avvenire SEMPRE, anche in caso di back button (bfcache)
		// dove il browser ripristina i valori dei form ma non riesegue gli handler.
		applyStateFromSelection();

		// Evito di attaccare listener duplicati, ma mantengo comunque il riallineamento sopra.
		if (documentTypeSelect.dataset.fiscalDocumentInitialized === 'true') return;
		documentTypeSelect.dataset.fiscalDocumentInitialized = 'true';

		// Cambio select -> riallineo campi
		documentTypeSelect.addEventListener('change', applyStateFromSelection);
		documentTypeSelect.addEventListener('change', saveToSessionStorage);

		// Input -> salvo in sessionStorage (così non si perde il valore navigando via link)
		;['input', 'change', 'keyup'].forEach((eventType) => {
			codiceFiscaleInput.addEventListener(eventType, saveToSessionStorage);
			partitaIvaInput.addEventListener(eventType, saveToSessionStorage);
			codiceSdiInput.addEventListener(eventType, saveToSessionStorage);
		});

		// Click checkout -> forzo il riallineamento e blocco checkout se il campo required non è valido
		// (necessario perché nel tema il form cart ha `novalidate`).
		if (checkoutButton) {
			checkoutButton.addEventListener('click', (event) => {
				applyStateFromSelection();

				const codiceFiscaleOk = codiceFiscaleInput.checkValidity();
				const partitaIvaOk = partitaIvaInput.checkValidity();

				if (codiceFiscaleInput.hasAttribute('required') && !codiceFiscaleOk) {
					event.preventDefault();
					codiceFiscaleInput.reportValidity();
					return;
				}

				if (partitaIvaInput.hasAttribute('required') && !partitaIvaOk) {
					event.preventDefault();
					partitaIvaInput.reportValidity();
					return;
				}
			});
		}
	};

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', () => syncFiscalDocumentFields(document));
	} else {
		syncFiscalDocumentFields(document);
	}

	// pageshow viene triggerato anche quando si torna indietro/avanti (bfcache): riallineo sempre.
	window.addEventListener('pageshow', () => syncFiscalDocumentFields(document));

	const cartForm = document.querySelector('cart-form');
	if (cartForm) {
		cartForm.addEventListener('cart-updated', () => syncFiscalDocumentFields(document));
	}
})();
